import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { z } from 'zod';

import MeetingCard from '../components/MeetingCard';
import { contentQueries, useGetContent } from '../lib/react-query/ContentQueries';
import { meetingQueries, useGetMeetingById } from '../lib/react-query/MeetingQueries';

const searchParamsSchema = z.object({
  id: z.string(),
  token: z.string().optional(),
});

export const Route = createFileRoute('/modification')({
  component: ModificationPage,
  validateSearch: (search) => {
    const result = searchParamsSchema.safeParse(search);
    if (!result.success) {
      throw redirect({ to: '/' });
    }
    return result.data;
  },
  loaderDeps: ({ search }) => ({ id: search.id }),
  loader: async ({ context, deps }) => {
    const { queryClient } = context;
    const { id } = deps;

    const [meeting] = await Promise.all([
      queryClient.ensureQueryData(meetingQueries.byId(id)).catch(() => {
        throw redirect({ to: '/' });
      }),
      queryClient.ensureQueryData(contentQueries.main()),
    ]);

    if (meeting.is_cancelled || meeting.has_missed) {
      throw redirect({ to: '/' });
    }
  },
  head: () => ({
    meta: [
      {
        title: 'Modification de rendez-vous | Azelysse Piercing & Tattoo',
      },
      {
        name: 'robots',
        content: 'noindex, nofollow',
      },
    ],
  }),
});

function ModificationPage() {
  const { id, token } = Route.useSearch();
  const navigate = useNavigate();
  const { data: content } = useGetContent();
  const { data: meeting, isFetching, isError } = useGetMeetingById(id);

  const meetingToken = token || meeting?.access_token;

  useEffect(() => {
    if (!id || (isError && !isFetching)) {
      navigate({ to: '/' });
    }
  }, [id, isError, isFetching, navigate]);

  useEffect(() => {
    if (meeting && (meeting.is_cancelled || meeting.has_missed)) {
      navigate({ to: '/' });
    }
  }, [meeting, navigate]);

  if (isFetching) {
    return (
      <div className="mx-auto max-w-4xl px-6 pt-36 pb-32 sm:pt-48 lg:pt-32">
        <p>Chargement...</p>
      </div>
    );
  }

  if (!meeting || !content) {
    return null;
  }

  return (
    <div className="mx-auto max-w-4xl px-6 pt-36 pb-32 sm:pt-48 lg:pt-32">
      <h1 className="mb-8 text-2xl font-semibold">Modification de vôtre rendez-vous</h1>

      <div className="mt-8">
        <MeetingCard meeting={meeting} content={content} showButtons token={meetingToken} />
      </div>
    </div>
  );
}
